import { useCallback, useEffect, useState, type FormEvent } from "react";
import { CircleCheck, HelpCircle, Wallet } from "lucide-react";
import { Alert, Badge, Button, Card, CardBody, EmptyState, Skeleton, useToast, type Tone } from "@/ui";
import { KarigarShell } from "@/components/KarigarShell";
import { WithdrawHelp } from "@/components/WithdrawHelp";
import { PointsBalanceCard } from "@/components/PointsBalanceCard";
import {
  REDEMPTION_LABEL,
  cancelRedemption,
  fetchPoints,
  fetchRedemptions,
  redeemErrorText,
  requestRedemption,
  type Redemption,
  type RedemptionStatus,
} from "@/lib/karigarPoints";
import { useLanguage } from "@/i18n/language";
import { api } from "@/lib/api";
import type { UserDto } from "@gatiq/shared";

/**
 * Turning points into a payout — WIREFRAMES.md "Karigar redeem".
 *
 * The code is the whole screen. A karigar stands at a counter and reads six
 * digits to a shopkeeper who is waiting, so it is shown large, monospaced and
 * widely spaced, and it stays on the list until the money is actually paid.
 *
 * It is deliberately not WhatsApp-only. The points leave the ledger the moment
 * a withdrawal is requested, so a karigar whose message never arrives would be
 * left with a debit and no way to finish — and WhatsApp here is one shared
 * account that can be unlinked or down. The message still goes; this screen is
 * what makes it optional.
 */
const TONE: Record<RedemptionStatus, Tone> = {
  pending: "warning",
  settled: "success",
  cancelled: "neutral",
  expired: "neutral",
};

export function KarigarRedeemPage() {
  const toast = useToast();
  const { t, pick } = useLanguage("hi");
  const [balance, setBalance] = useState<number | null>(null);
  const [rows, setRows] = useState<Redemption[] | null>(null);
  const [loading, setLoading] = useState(true);

  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ hi: string; en: string } | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // Withdrawing opens only after approval; a pending karigar is told so up front.
  const [approved, setApproved] = useState<boolean | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [points, redemptions] = await Promise.all([fetchPoints(1, 1), fetchRedemptions()]);
      setBalance(points.balance);
      setRows(redemptions.items);
    } catch {
      // The screen keeps its last good state; the error surfaces on action.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    api
      .get<UserDto>("/org/me/karigar")
      .then((me) => setApproved(me.kycStatus === "approved"))
      .catch(() => setApproved(null));
  }, [load]);

  const wanted = Number(amount);
  const valid = Number.isInteger(wanted) && wanted > 0 && wanted <= (balance ?? 0);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    setBusy(true);
    try {
      await requestRedemption(wanted);
      setAmount("");
      await load();
    } catch (err) {
      setError(redeemErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id: string): Promise<void> {
    setCancelling(id);
    try {
      await cancelRedemption(id);
      toast.success(t("अंक वापस मिल गए", "Points returned"));
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not cancel.");
    } finally {
      setCancelling(null);
    }
  }

  const pending = rows?.filter((r) => r.status === "pending") ?? [];
  const past = rows?.filter((r) => r.status !== "pending") ?? [];

  return (
    <KarigarShell active="redeem" title={t("QCoin रिडीम करें", "Redeem QCoin")}>
      <PointsBalanceCard balance={balance} loading={loading && balance === null} />

      {/* The open request comes first: somebody is waiting to be shown a code. */}
      {pending.map((r) => (
        <Card key={r.id}>
          <CardBody className="flex flex-col items-center gap-2 py-6 text-center">
            <CircleCheck aria-hidden="true" className="size-8 text-success" />
            <p className="font-title text-lg font-semibold text-ink">
              {r.points} QCoin <span className="font-normal text-muted">· {r.displayId}</span>
            </p>
            <p className="text-sm text-muted">
              {t(
                "यह कोड दुकानदार को बताएं — जल्दी नहीं, यह खत्म नहीं होता।",
                "Read this code to your shopkeeper. No rush — it does not expire.",
              )}
            </p>

            {r.otp ? (
              <p
                className="my-2 rounded-lg border-2 border-dashed border-brand-border bg-brand-light py-4 pr-5 pl-7 font-mono text-4xl font-bold tracking-[0.3em] text-brand-ink tabular-nums"
                /* Announced as well as shown — this is the moment the money
                   changes hands, and it is read aloud, not just looked at. */
                role="status"
              >
                {r.otp}
              </p>
            ) : null}

            <div className="flex flex-col items-center gap-1">
              <Button
                icon={<HelpCircle aria-hidden="true" className="size-4" />}
                onClick={() => setHelpOpen(true)}
                size="sm"
                variant="secondary"
              >
                {t("दुकान कहाँ है?", "Where do I go?")}
              </Button>
              <Button
                loading={cancelling === r.id}
                onClick={() => void cancel(r.id)}
                size="sm"
                variant="ghost"
              >
                {t("रद्द करें", "Cancel")}
              </Button>
            </div>
          </CardBody>
        </Card>
      ))}

      {pending.length === 0 && approved === false ? (
        <Alert kind="info" title={t("मंज़ूरी का इंतज़ार", "Waiting for approval")}>
          {t(
            "आप स्कैन करते रह सकते हैं — आपके QCoin सुरक्षित हैं। मंज़ूरी मिलते ही QCoin रिडीम कर सकेंगे।",
            "You can keep scanning — your QCoin are safe. You can redeem them once you are approved.",
          )}
        </Alert>
      ) : pending.length === 0 ? (
        <Card>
          <CardBody>
            <form className="flex flex-col gap-3" noValidate onSubmit={(e) => void submit(e)}>
              <p className="text-sm font-medium text-ink">{t("कितने QCoin रिडीम करने हैं?", "How many QCoin do you want to redeem?")}</p>

              <div className="flex items-center gap-2">
                <span className="font-title text-lg text-muted">QCoin </span>
                <input
                  aria-label={t("राशि", "Amount")}
                  className="h-12 w-full rounded-md border border-rule bg-raised px-3 font-title text-lg text-ink shadow-control transition-[border-color,box-shadow] placeholder:text-muted/50 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
                  inputMode="numeric"
                  onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
                  placeholder="500"
                  value={amount}
                />
              </div>

              {balance !== null && wanted > balance ? (
                <p className="text-sm text-danger">
                  {t(`आपके पास ${balance} QCoin हैं`, `You have ${balance} QCoin`)}
                </p>
              ) : null}

              {error ? (
                <Alert kind="danger">{pick(error)}</Alert>
              ) : null}

              <Button
                block
                disabled={!valid}
                icon={<Wallet aria-hidden="true" className="size-4" />}
                loading={busy}
                type="submit"
              >
                {t("कोड लें", "Get the code")}
              </Button>

              <Button
                block
                icon={<HelpCircle aria-hidden="true" className="size-4" />}
                onClick={() => setHelpOpen(true)}
                type="button"
                variant="secondary"
              >
                {t("पैसे कैसे मिलेंगे?", "How do I get my money?")}
              </Button>
            </form>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody className="p-0">
          <p className="border-b border-rule/70 px-4 pt-3.5 pb-3 font-title text-sm font-semibold text-ink">
            {t("पिछले रिडेम्प्शन", "Past redemptions")}
          </p>

          {loading && rows === null ? (
            <div className="flex flex-col gap-3 p-4">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : past.length > 0 ? (
            <ul className="divide-y divide-rule">
              {past.map((r) => {
                const label = REDEMPTION_LABEL[r.status];
                return (
                  <li className="flex items-center justify-between gap-3 px-4 py-3.5" key={r.id}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink">{r.points} QCoin</p>
                      <p className="truncate text-xs text-muted">{pick(label)}</p>
                    </div>
                    <Badge tone={TONE[r.status]}>{r.displayId ?? ""}</Badge>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState
              message={t("पहले QCoin रिडेम्प्शन के बाद यहाँ दिखेगा।", "Your QCoin redemptions will appear here.")}
              title={t("अभी कुछ नहीं", "Nothing yet")}
            />
          )}
        </CardBody>
      </Card>
      <WithdrawHelp onClose={() => setHelpOpen(false)} open={helpOpen} />
    </KarigarShell>
  );
}
